import * as React from 'react';
import useRestService from '../../shared/rest-service';
import { Editor, IAllProps } from '@tinymce/tinymce-react';

interface Props extends IAllProps {
  // Provide a ref in order to access editor properties, i.e. the text content
  editorRef: React.MutableRefObject<any>;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void; //Formik function to set the field value
  setField?: string; //The field param used in the setFieldValue function call
}

/**
 * TinyEditor
 * Wrapper around the tinymce react component to provide the self-hosted tinymce assets instead of bundling them.
 * This prevents scripts and styles from being packaged directly in index js and css files and instead only loads them
 * when needed in the admin side, without polluting the global css scope.
 * See tiny.cloud/docs/integrations/react/#tinymcereacttechnicalreference for full Editor component reference.
 */
const TinyEditor = ({ editorRef, setField, setFieldValue, init, ...rest }: Props) => {
  const { postFileAuth } = useRestService();

  return (
    <Editor
      tinymceScriptSrc={`${document.baseURI}tinymce/tinymce.min.js`}
      onInit={(_evt, editor) => (editorRef.current = editor)}
      init={{
        promotion: false,
        height: '300px',
        table_resize_bars: true,
        // This option specifies whether data:url images (inline images) should be removed or not from
        // the pasted contents. Setting paste_data_images to "true" will allow the pasted images, while
        // setting it to "false" will disallow pasted images.
        paste_data_images: true,
        images_upload_url: window.env.BASE_API + 'api/help-documents/image-upload',
        images_upload_handler: (blobInfo) =>
          new Promise((resolve, reject) => {
            postFileAuth('api/help-documents/image-upload', null, blobInfo.blob())
              .then((result) => {
                resolve(result.message);
              })
              .catch(() => {
                reject('Failed to upload image.');
              });
          }),
        image_advtab: true,
        font_formats: 'Montserrat="Montserrat",sans-serif; OpenSans="Open Sans",sans-serif',
        table_class_list: [
          { title: 'Default', value: 'styledTable' },
          { title: 'Un-styled', value: 'unstyledTable' }
        ],
        table_default_attributes: { class: 'styledTable' },
        setup: (editor) => {
          editor.on('input change', () => {
            const content = editor.getContent();
            if (!!setFieldValue && !!setField) {
              setFieldValue(setField, content);
            }
          });

          editor.on('ExecCommand', (event) => {
            // the following block of code is used to fix the issue with TinyMCE's
            // FullPage plugin not triggering a change event on Save
            if (event.command === 'mceFullPageProperties' && !!setFieldValue && !!setField) {
              // find tinymce dialog that contains title 'Metadata'
              const metaTitles = Array.from(document.getElementsByClassName('tox-dialog__title')).filter((e) =>
                e.textContent.includes('Metadata')
              );
              if (metaTitles.length > 0) {
                // find the Save button
                const saveButtons = Array.from(
                  metaTitles[0].parentElement.parentElement.getElementsByTagName('button')
                ).filter((e) => e.textContent.includes('Save'));
                if (saveButtons.length > 0) {
                  // update content on click
                  saveButtons[0].addEventListener('click', (_) => {
                    // this click handler relies on the FullPage plugin's internal click handler being
                    // triggered first, seTimeout is used to ensure that this handler gets called the last
                    setTimeout((_) => setFieldValue(setField, editor.getContent()), 1);
                  });
                }
              }
            }
          });
        },
        highlight_on_focus: false,
        ...init
      }}
      {...rest}
    />
  );
};

export default TinyEditor;
